/**
 * Looks listos para los botones, las tarjetas y las secciones del sitio.
 *
 * Como las combinaciones, pero por familia y por partes: la forma, la sombra,
 * la sombra interna, el tamaño y el texto se eligen por separado y se suman.
 * Cada grupo toca SÓLO sus propiedades, así que elegir una sombra no deshace
 * la forma que ya pusiste. Son las mismas propiedades que ajusta el popup del
 * estilo, escritas como él las lee: lo que pone un look se sigue editando a
 * mano después.
 *
 * La regla del color: un look nunca trae uno propio. Sale del botón principal
 * o de la paleta, así que cambiar de paleta después sigue repintándolo todo.
 * Se aplica a los tokens, no a un elemento: cambia todo lo que sigue al estilo.
 */
import type { StyleProperties } from '@webbuilder/style-engine'
import { outlinedCardStyles } from './outlinedCard'
import type { ProjectTheme } from './types'
import { mergeStylePatch } from './stylePatch'

type Patch = Partial<StyleProperties>
type Bp = 'desktop' | 'tablet' | 'mobile'

/* ── Sombras en el formato del popup ─────────────────────────────────────── */

/**
 * Una capa como la escribe el popup de estilo: cuatro medidas en px y el color
 * detrás. Con un `0` sin unidad, o el color delante, el popup leía otra cosa y
 * al tocar un control la sombra saltaba.
 */
function layer(x: number, y: number, blur: number, spread: number, color: string, inset = false) {
  return `${inset ? 'inset ' : ''}${x}px ${y}px ${blur}px ${spread}px ${color}`
}

/** Un color con transparencia, en la forma que el popup reconoce. */
function alpha(color: string, percent: number) {
  if (color.startsWith('var(')) return `color-mix(in srgb, ${color} ${percent}%, transparent)`
  const hex = /^#([0-9a-f]{6})$/i.exec(color)?.[1]
  if (hex) {
    const n = parseInt(hex, 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${percent / 100})`
  }
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`
}

function shadowLayers(value: string | undefined): string[] {
  if (!value || value.trim() === 'none') return []
  const layers: string[] = []
  let depth = 0
  let start = 0
  for (let index = 0; index < value.length; index += 1) {
    const char = value[index]
    if (char === '(') depth += 1
    else if (char === ')') depth = Math.max(0, depth - 1)
    else if (char === ',' && depth === 0) {
      layers.push(value.slice(start, index).trim())
      start = index + 1
    }
  }
  layers.push(value.slice(start).trim())
  return layers.filter(Boolean)
}

/**
 * Cambia la capa de fuera o la de dentro y deja la otra como estaba: la sombra
 * y la sombra interna viven en la misma propiedad pero se eligen por separado.
 */
export function withShadowLayer(current: string | undefined, next: string | null, inset: boolean): string {
  const others = shadowLayers(current).filter((item) => /\binset\b/i.test(item) !== inset)
  const outer = inset ? others : (next ? [next] : [])
  const inner = inset ? (next ? [next] : []) : others
  const layers = [...outer, ...inner]
  return layers.length ? layers.join(', ') : 'none'
}

/* ── Grupos ──────────────────────────────────────────────────────────────── */

/**
 * Una opción calcula su cambio a partir de lo que el token ya tiene: la sombra
 * tiene que conservar la capa interna, y el principal y el secundario salen
 * con los colores de la marca.
 */
export type LookOption<Ctx> = {
  id: string
  labelKey: string
  patch: (ctx: Ctx, current: StyleProperties) => Patch
  /** Lo mismo en tablet y móvil, cuando se reduce con la pantalla. */
  breakpoints?: Partial<Record<Exclude<Bp, 'desktop'>, Patch>>
}
export type LookGroup<Ctx> = { id: string; labelKey: string; options: LookOption<Ctx>[] }

/* ── Botones ─────────────────────────────────────────────────────────────── */

type ButtonColors = { accent: string; onAccent: string }
/** Qué botón se está calculando: el principal y el secundario llevan formas distintas. */
export type ButtonCtx = ButtonColors & { role: 'primary' | 'secondary' }

function isFill(value: string | undefined): value is string {
  return Boolean(value && value !== 'transparent' && value !== 'none' && !value.includes('gradient'))
}

/**
 * El color de la marca en los botones, sacado del principal: su fondo si es
 * sólido; si no, su borde o su texto. Y el del texto encima: el suyo si ya
 * era sólido —el cliente lo eligió contra ese fondo—; si no, el fondo del
 * sitio, que es lo que contrasta con un acento.
 */
export function buttonColorsOf(theme: ProjectTheme): ButtonColors {
  const styles = theme.buttonComponents.find((item) => item.id === 'button-primary')?.styles ?? {}
  const background = styles.backgroundColor ?? styles.background
  if (isFill(background)) return { accent: background, onAccent: styles.color ?? 'var(--wb-background)' }
  return { accent: styles.borderColor ?? styles.color ?? 'var(--wb-primary)', onAccent: 'var(--wb-background)' }
}

/* La forma borra lo que otra forma pudo dejar: lados sueltos, contorno y
   subrayado. Sin esto, pasar de «Línea inferior» a «Píldora» dejaba la línea. */
const NO_SIDES: Patch = { border: '', borderTop: '', borderRight: '', borderBottom: '', borderLeft: '', outline: '', outlineOffset: '', textDecoration: 'none' }

type Shape = (colors: ButtonColors) => Patch

/* El color de la tinta —la del texto— escrito como mezcla al 100%:
   `var(--wb-text)` a secas en el secundario es lo que `normalizeProjectTheme`
   toma por un tema antiguo y pasa a `--wb-muted`. Pinta igual y el popup lo
   lee como el color Texto. */
const INK = 'color-mix(in srgb, var(--wb-text) 100%, transparent)'

/* Un borde de 1px, no de 1.5: en una web de empresa el contorno grueso se lee
   como un botón de formulario de hace años. */
const solid = (radius: string): Shape => ({ accent, onAccent }) => ({
  ...NO_SIDES,
  background: accent, backgroundColor: accent, color: onAccent,
  borderWidth: '0px', borderStyle: 'solid', borderColor: accent, borderRadius: radius,
})
const outline = (radius: string): Shape => ({ accent }) => ({
  ...NO_SIDES,
  background: 'transparent', backgroundColor: 'transparent', color: accent,
  borderWidth: '1px', borderStyle: 'solid', borderColor: accent, borderRadius: radius,
})
const tinted = (radius: string): Shape => ({ accent }) => ({
  ...NO_SIDES,
  background: alpha(accent, 10), backgroundColor: alpha(accent, 10), color: accent,
  borderWidth: '0px', borderStyle: 'solid', borderColor: 'transparent', borderRadius: radius,
})
const textOnly = (radius: string, underline = false): Shape => ({ accent }) => ({
  ...NO_SIDES,
  ...(underline ? { textDecoration: 'underline' } : {}),
  background: 'transparent', backgroundColor: 'transparent', color: accent,
  borderWidth: '0px', borderStyle: 'solid', borderColor: 'transparent', borderRadius: radius,
})
/* Contorno de tinta, como los cuadernos y las pizarras de trabajo: el botón
   se dibuja con una línea fina del color del texto. */
const inked = (fill: 'accent' | 'page', width: string, radius: string): Shape => ({ accent, onAccent }) => ({
  ...NO_SIDES,
  background: fill === 'accent' ? accent : 'var(--wb-background)',
  backgroundColor: fill === 'accent' ? accent : 'var(--wb-background)',
  color: fill === 'accent' ? onAccent : INK,
  borderWidth: width, borderStyle: 'solid', borderColor: 'var(--wb-text)', borderRadius: radius,
})
const shape = (id: string, primary: Shape, secondary: Shape): LookOption<ButtonCtx> => ({
  id,
  labelKey: `looks.button.${id}`,
  patch: (ctx) => (ctx.role === 'primary' ? primary : secondary)(ctx),
})

/**
 * Un secundario sin fondo ni borde —sólo texto— no lleva sombra: sería una
 * mancha alrededor de una palabra suelta.
 */
function hasBox(styles: StyleProperties) {
  const background = styles.backgroundColor ?? styles.background
  const width = parseFloat(String(styles.borderWidth ?? '0'))
  return isFill(background) || width > 0 || Boolean(styles.borderBottom || styles.borderTop || styles.outline)
}
const outer = (id: string, make: ((ctx: ButtonCtx) => string) | null): LookOption<ButtonCtx> => ({
  id,
  labelKey: `looks.shadow.${id}`,
  patch: (ctx, current) => ({
    boxShadow: withShadowLayer(current.boxShadow, make && (ctx.role === 'primary' || hasBox(current)) ? make(ctx) : null, false),
  }),
})
const inner = (id: string, make: ((ctx: ButtonCtx) => string) | null): LookOption<ButtonCtx> => ({
  id,
  labelKey: `looks.inner.${id}`,
  patch: (ctx, current) => ({
    boxShadow: withShadowLayer(current.boxShadow, make && (ctx.role === 'primary' || hasBox(current)) ? make(ctx) : null, true),
  }),
})
/* El tamaño es relleno y alto mínimo. La letra no: va siempre al tamaño del
   párrafo (ver `buttonTextSize`), y sólo la cambia quien la toque a mano. */
const size = (id: string, y: number, x: number, minHeight: number): LookOption<ButtonCtx> => ({
  id,
  labelKey: `looks.size.${id}`,
  patch: () => ({
    /* El atajo con los mismos valores, no borrado: un botón de fábrica que
       pierde una propiedad la recupera al cargar con su valor de fábrica. */
    padding: `${y}px ${x}px`, paddingTop: `${y}px`, paddingBottom: `${y}px`, paddingLeft: `${x}px`, paddingRight: `${x}px`,
    minHeight: `${minHeight}px`,
  }),
})
const text = (id: string, transform: string, spacing: string): LookOption<ButtonCtx> => ({
  id,
  labelKey: `looks.text.${id}`,
  patch: () => ({ textTransform: transform, letterSpacing: spacing }),
})
const weight = (id: string, value: string): LookOption<ButtonCtx> => ({
  id,
  labelKey: `looks.weight.${id}`,
  patch: () => ({ fontWeight: value }),
})

/**
 * Lo que usan hoy las webs de producto y de empresa, sin lo que envejece mal:
 * nada de resplandor de color, contornos gruesos, punteados ni relieves de
 * juguete. Sombras finas y neutras, y la sombra plana de tinta —la «2D» de los
 * cuadernos y del brutalismo— para quien busca carácter.
 */
export const BUTTON_LOOK_GROUPS: LookGroup<ButtonCtx>[] = [
  {
    id: 'shape',
    labelKey: 'looks.group.shape',
    options: [
      shape('pill', solid('999px'), outline('999px')),
      shape('rounded', solid('10px'), outline('10px')),
      shape('square', solid('6px'), outline('6px')),
      shape('sharp', solid('0px'), outline('0px')),
      shape('tonal', solid('999px'), tinted('999px')),
      shape('tonalRounded', solid('10px'), tinted('10px')),
      shape('outlinePill', outline('999px'), textOnly('999px')),
      shape('outline', outline('8px'), textOnly('8px')),
      shape('ghost', solid('8px'), textOnly('8px')),
      shape('link', solid('999px'), textOnly('999px', true)),
      shape('inked', inked('accent', '1px', '8px'), inked('page', '1px', '8px')),
      shape('brutal', inked('accent', '2px', '4px'), inked('page', '2px', '4px')),
    ],
  },
  {
    id: 'shadow',
    labelKey: 'looks.group.shadow',
    options: [
      outer('none', null),
      outer('hairline', () => layer(0, 1, 2, 0, 'rgba(15, 23, 42, 0.06)')),
      outer('subtle', () => layer(0, 1, 3, 0, 'rgba(15, 23, 42, 0.12)')),
      outer('soft', () => layer(0, 4, 12, -2, 'rgba(15, 23, 42, 0.14)')),
      outer('medium', () => layer(0, 10, 24, -8, 'rgba(15, 23, 42, 0.24)')),
      outer('hard2d', () => layer(2, 2, 0, 0, 'var(--wb-text)')),
      outer('hard', () => layer(4, 4, 0, 0, 'var(--wb-text)')),
    ],
  },
  {
    id: 'inner',
    labelKey: 'looks.group.inner',
    options: [
      inner('none', null),
      inner('highlight', () => layer(0, 1, 0, 0, 'rgba(255, 255, 255, 0.18)', true)),
      inner('ring', () => layer(0, 0, 0, 1, 'rgba(255, 255, 255, 0.12)', true)),
      inner('bevel', () => layer(0, -1, 0, 0, 'rgba(0, 0, 0, 0.14)', true)),
    ],
  },
  {
    id: 'size',
    labelKey: 'looks.group.size',
    options: [
      size('small', 7, 14, 34),
      size('compact', 9, 16, 38),
      size('regular', 12, 22, 44),
      size('large', 15, 28, 52),
      /* Ancho, pero no tanto: con 36 px a cada lado y letra espaciada, un texto
         de cuatro palabras ocupaba media columna y el segundo botón no cabía. */
      size('wide', 13, 28, 46),
    ],
  },
  {
    id: 'text',
    labelKey: 'looks.group.text',
    options: [
      text('normal', 'none', '0'),
      text('tight', 'none', '-0.01em'),
      text('uppercase', 'uppercase', '0.06em'),
      /* 0.1em y no 0.14: espaciado se sigue viendo distinto de «mayúsculas»
         (0.06), pero sin ensanchar el botón casi un 15 %. */
      text('spaced', 'uppercase', '0.1em'),
    ],
  },
  {
    id: 'weight',
    labelKey: 'looks.group.weight',
    options: [
      weight('regular', '400'),
      weight('medium', '500'),
      weight('semibold', '600'),
      weight('bold', '700'),
    ],
  },
]

export type ButtonLook = LookOption<ButtonCtx>
const BUTTON_ROLES = [['button-primary', 'primary'], ['button-secondary', 'secondary']] as const

function buttonStyles(theme: ProjectTheme, id: string): StyleProperties {
  return theme.buttonComponents.find((item) => item.id === id)?.styles ?? {}
}

/** Lo que una opción le haría a cada botón, calculado sobre cómo está ahora. */
export function buttonLookPatches(theme: ProjectTheme, look: ButtonLook) {
  const colors = buttonColorsOf(theme)
  return {
    primary: look.patch({ ...colors, role: 'primary' }, buttonStyles(theme, 'button-primary')),
    secondary: look.patch({ ...colors, role: 'secondary' }, buttonStyles(theme, 'button-secondary')),
  }
}

/* ── Tarjetas ────────────────────────────────────────────────────────────── */

type CardCtx = { colorCard: boolean }

const CARD_NO_SIDES: Patch = { border: '', borderTop: '', borderRight: '', borderBottom: '', borderLeft: '', outline: '', outlineOffset: '' }

/**
 * La tarjeta de color lleva el acento de fondo y de borde: de una forma sólo
 * toma el radio, o se quedaría con un borde gris sobre su color.
 */
const cardShape = (id: string, patch: Patch): LookOption<CardCtx> => ({
  id,
  labelKey: `looks.card.${id}`,
  patch: ({ colorCard }) => colorCard
    ? (patch.borderRadius ? { borderRadius: patch.borderRadius } : {})
    : { ...CARD_NO_SIDES, borderStyle: 'solid', ...patch },
})
const cardOuter = (id: string, value: string | null): LookOption<CardCtx> => ({
  id,
  labelKey: `looks.shadow.${id}`,
  patch: (_ctx, current) => ({ boxShadow: withShadowLayer(current.boxShadow, value, false) }),
})
const cardInner = (id: string, value: string | null): LookOption<CardCtx> => ({
  id,
  labelKey: `looks.inner.${id}`,
  patch: (_ctx, current) => ({ boxShadow: withShadowLayer(current.boxShadow, value, true) }),
})
const pad = (value: number): Patch => ({
  padding: `${value}px`, paddingTop: `${value}px`, paddingRight: `${value}px`, paddingBottom: `${value}px`, paddingLeft: `${value}px`,
})
/* El relleno sí baja por tamaños: 56px a cada lado en un móvil dejan el texto
   en una columna de nada. */
const cardSpacing = (id: string, desktop: number, tablet: number, mobile: number): LookOption<CardCtx> => ({
  id,
  labelKey: `looks.spacing.${id}`,
  patch: () => pad(desktop),
  breakpoints: { tablet: pad(tablet), mobile: pad(mobile) },
})

const BORDER = 'var(--wb-border)'

export const CARD_LOOK_GROUPS: LookGroup<CardCtx>[] = [
  {
    id: 'shape',
    labelKey: 'looks.group.shape',
    options: [
      cardShape('hairline', { borderWidth: '1px', borderColor: alpha(BORDER, 70), borderRadius: '12px' }),
      cardShape('outlined', { borderWidth: '1px', borderColor: BORDER, borderRadius: '16px' }),
      cardShape('box', { borderWidth: '1px', borderColor: BORDER, borderRadius: '8px' }),
      cardShape('soft', { borderWidth: '0px', borderColor: 'transparent', borderRadius: '20px' }),
      cardShape('rounded', { borderWidth: '0px', borderColor: 'transparent', borderRadius: '28px' }),
      cardShape('flat', { borderWidth: '0px', borderColor: 'transparent', borderRadius: '8px' }),
      cardShape('square', { borderWidth: '0px', borderColor: 'transparent', borderRadius: '0px' }),
      cardShape('sharp', { borderWidth: '1px', borderColor: BORDER, borderRadius: '0px' }),
      cardShape('ink', { borderWidth: '1px', borderColor: 'var(--wb-text)', borderRadius: '12px' }),
      cardShape('bold', { borderWidth: '2px', borderColor: 'var(--wb-text)', borderRadius: '6px' }),
    ],
  },
  {
    id: 'shadow',
    labelKey: 'looks.group.shadow',
    options: [
      cardOuter('none', null),
      cardOuter('hairline', layer(0, 1, 2, 0, 'rgba(15, 23, 42, 0.05)')),
      cardOuter('subtle', layer(0, 2, 8, -2, 'rgba(15, 23, 42, 0.08)')),
      cardOuter('soft', layer(0, 8, 24, -12, 'rgba(15, 23, 42, 0.14)')),
      cardOuter('medium', layer(0, 16, 32, -16, 'rgba(15, 23, 42, 0.2)')),
      cardOuter('large', layer(0, 28, 56, -24, 'rgba(15, 23, 42, 0.26)')),
      cardOuter('hard2d', layer(4, 4, 0, 0, 'var(--wb-text)')),
      cardOuter('hard', layer(6, 6, 0, 0, 'var(--wb-text)')),
    ],
  },
  {
    id: 'inner',
    labelKey: 'looks.group.inner',
    options: [
      cardInner('none', null),
      cardInner('highlight', layer(0, 1, 0, 0, 'rgba(255, 255, 255, 0.07)', true)),
      cardInner('ring', layer(0, 0, 0, 1, 'rgba(255, 255, 255, 0.06)', true)),
      cardInner('soft', layer(0, 1, 2, 0, 'rgba(15, 23, 42, 0.04)', true)),
    ],
  },
  {
    id: 'spacing',
    labelKey: 'looks.group.spacing',
    options: [
      cardSpacing('compact', 16, 14, 12),
      cardSpacing('regular', 24, 22, 20),
      cardSpacing('comfortable', 32, 28, 24),
      cardSpacing('roomy', 40, 32, 28),
      cardSpacing('spacious', 56, 44, 32),
    ],
  },
]

export type CardLook = LookOption<CardCtx>
/* Las de fábrica que no son de color se llevan el look entero; la de color, lo
   que dice `cardShape`. Las que crea el cliente no se tocan: son suyas. */
const CARD_TARGETS: Record<string, CardCtx> = {
  'card-default': { colorCard: false },
  'card-soft': { colorCard: false },
  'card-elevated': { colorCard: true },
}

/* ── Aplicar y reconocer ─────────────────────────────────────────────────── */

/** Lo que ya tiene el token coincide con el cambio: es la opción que está puesta. */
function matches(styles: StyleProperties | undefined, patch: Patch) {
  const keys = Object.keys(patch)
  return keys.length > 0 && keys.every((key) => {
    const value = patch[key as keyof StyleProperties]
    const current = styles?.[key as keyof StyleProperties]
    if (key === 'boxShadow') return (current ?? 'none') === value
    return value === '' ? current === undefined : current === value
  })
}

/**
 * La opción se reconoce por su forma —radio, grosor, relleno—, no por el
 * color de la letra y del contorno: un estilo puede pasarlos a la tinta (ver
 * `inkButtons` en los estilos de diseño) y la forma sigue siendo la misma.
 */
function withoutInk(patch: Patch): Patch {
  const { color: _color, borderColor: _borderColor, ...rest } = patch
  return rest
}

export function activeButtonLookId(theme: ProjectTheme, group: LookGroup<ButtonCtx>): string | null {
  return group.options.find((look) => {
    const patches = buttonLookPatches(theme, look)
    return matches(buttonStyles(theme, 'button-primary'), withoutInk(patches.primary))
      && matches(buttonStyles(theme, 'button-secondary'), withoutInk(patches.secondary))
  })?.id ?? null
}

export function activeCardLookId(theme: ProjectTheme, group: LookGroup<CardCtx>): string | null {
  const base = theme.cardComponents.find((item) => item.id === 'card-default')?.styles ?? {}
  return group.options.find((look) => matches(base, look.patch({ colorCard: false }, base)))?.id ?? null
}

/**
 * Pone una opción en el tema, en escritorio: tablet y móvil lo heredan salvo lo
 * que la opción baje por tamaños. Los tokens de fábrica llevan su rol pegado
 * —el que pinta los elementos sin token—, y el rol se toca a la vez o la mitad
 * del sitio se quedaría con el look de antes.
 */
export function applyButtonLookToTheme(theme: ProjectTheme, look: ButtonLook): ProjectTheme {
  const colors = buttonColorsOf(theme)
  const roles = { ...theme.roles }
  const buttonComponents = theme.buttonComponents.map((item) => {
    const role = BUTTON_ROLES.find(([id]) => id === item.id)?.[1]
    if (!role) return item
    const patch = look.patch({ ...colors, role }, item.styles)
    if (item.role) roles[item.role] = mergeStylePatch(roles[item.role], patch)
    return { ...item, styles: mergeStylePatch(item.styles, patch) }
  })
  return { ...theme, presetId: 'custom', roles, buttonComponents }
}

export function applyCardLookToTheme(theme: ProjectTheme, look: CardLook): ProjectTheme {
  let roles = theme.roles
  let roleBreakpoints = theme.roleBreakpoints
  const cardComponents = theme.cardComponents.map((item) => {
    const ctx = CARD_TARGETS[item.id]
    if (!ctx) return item
    const patch = look.patch(ctx, item.styles)
    const small = look.breakpoints
    if (item.id === 'card-default') {
      roles = { ...roles, card: mergeStylePatch(roles.card, patch) }
      if (small) {
        roleBreakpoints = {
          ...roleBreakpoints,
          ...(small.tablet ? { tablet: { ...roleBreakpoints?.tablet, card: mergeStylePatch(roleBreakpoints?.tablet?.card, small.tablet) } } : {}),
          ...(small.mobile ? { mobile: { ...roleBreakpoints?.mobile, card: mergeStylePatch(roleBreakpoints?.mobile?.card, small.mobile) } } : {}),
        }
      }
    }
    const merged = mergeStylePatch(item.styles, patch)
    return {
      ...item,
      styles: item.id === 'card-soft' ? outlinedCardStyles(merged) : merged,
      ...(small
        ? {
            breakpoints: {
              ...item.breakpoints,
              ...(small.tablet ? { tablet: mergeStylePatch(item.breakpoints?.tablet, small.tablet) } : {}),
              ...(small.mobile ? { mobile: mergeStylePatch(item.breakpoints?.mobile, small.mobile) } : {}),
            },
          }
        : {}),
    }
  })
  return { ...theme, presetId: 'custom', roles, ...(roleBreakpoints ? { roleBreakpoints } : {}), cardComponents }
}

/* ── Secciones ───────────────────────────────────────────────────────────── */

/**
 * Cuánto aire hay entre bloques. Sin fondos: el color de las secciones ya lo
 * decide la paleta, y el estilo de sección no lleva el color del texto, así
 * que un fondo oscuro dejaría los títulos sin leerse.
 *
 * Aquí el espacio sí baja por tamaños: 136px arriba y abajo en un móvil se
 * come la pantalla, y dejarlo heredar sería peor que no tocarlo.
 */
export type SectionSpacingLook = { id: string; labelKey: string; padding: Record<Bp, string> }

export const SECTION_SPACING_LOOKS: SectionSpacingLook[] = [
  { id: 'tight', labelKey: 'looks.section.tight', padding: { desktop: '48px', tablet: '40px', mobile: '32px' } },
  { id: 'compact', labelKey: 'looks.section.compact', padding: { desktop: '72px', tablet: '56px', mobile: '44px' } },
  { id: 'balanced', labelKey: 'looks.section.balanced', padding: { desktop: '104px', tablet: '80px', mobile: '60px' } },
  { id: 'airy', labelKey: 'looks.section.airy', padding: { desktop: '136px', tablet: '104px', mobile: '72px' } },
  { id: 'spacious', labelKey: 'looks.section.spacious', padding: { desktop: '176px', tablet: '128px', mobile: '88px' } },
]

const SECTION_IDS = ['section-default', 'section-secondary', 'section-accent']

export function activeSectionSpacingId(theme: ProjectTheme): string | null {
  const styles = theme.sectionComponents.find((item) => item.id === 'section-default')?.styles
  return SECTION_SPACING_LOOKS.find((look) => styles?.paddingTop === look.padding.desktop && styles?.paddingBottom === look.padding.desktop)?.id ?? null
}

export function applySectionSpacingToTheme(theme: ProjectTheme, look: SectionSpacingLook): ProjectTheme {
  const sectionPad = (bp: Bp): Patch => ({ padding: '', paddingTop: look.padding[bp], paddingBottom: look.padding[bp] })
  let roles = theme.roles
  let roleBreakpoints = theme.roleBreakpoints
  const sectionComponents = theme.sectionComponents.map((item) => {
    if (!SECTION_IDS.includes(item.id)) return item
    if (item.id === 'section-default') {
      roles = { ...roles, section: mergeStylePatch(roles.section, sectionPad('desktop')) }
      roleBreakpoints = {
        ...roleBreakpoints,
        tablet: { ...roleBreakpoints?.tablet, section: mergeStylePatch(roleBreakpoints?.tablet?.section, sectionPad('tablet')) },
        mobile: { ...roleBreakpoints?.mobile, section: mergeStylePatch(roleBreakpoints?.mobile?.section, sectionPad('mobile')) },
      }
    }
    return {
      ...item,
      styles: mergeStylePatch(item.styles, sectionPad('desktop')),
      breakpoints: {
        ...item.breakpoints,
        tablet: mergeStylePatch(item.breakpoints?.tablet, sectionPad('tablet')),
        mobile: mergeStylePatch(item.breakpoints?.mobile, sectionPad('mobile')),
      },
    }
  })
  return { ...theme, presetId: 'custom', roles, ...(roleBreakpoints ? { roleBreakpoints } : {}), sectionComponents }
}

/* ── Separación de la barra de navegación ────────────────────────────────── */

/**
 * Cómo se separa la barra de la página: con una línea debajo —fina, de tinta,
 * gruesa—, con un fondo algo distinto, o con las dos cosas. Toca el estilo de
 * sección «Navegación», así que vale para la barra de todo el sitio.
 */
export type NavSeparationLook = { id: string; labelKey: string; background: string; line: string | null }

export const NAV_SEPARATION_LOOKS: NavSeparationLook[] = [
  { id: 'none', labelKey: 'looks.nav.none', background: 'var(--wb-background)', line: null },
  { id: 'line', labelKey: 'looks.nav.line', background: 'var(--wb-background)', line: '1px solid var(--wb-border)' },
  { id: 'ink', labelKey: 'looks.nav.ink', background: 'var(--wb-background)', line: '1px solid var(--wb-text)' },
  { id: 'bold', labelKey: 'looks.nav.bold', background: 'var(--wb-background)', line: '2px solid var(--wb-text)' },
  { id: 'tinted', labelKey: 'looks.nav.tinted', background: 'var(--wb-backgroundSecondary)', line: null },
  { id: 'tintedLine', labelKey: 'looks.nav.tintedLine', background: 'var(--wb-backgroundSecondary)', line: '1px solid var(--wb-border)' },
]

function navSeparationPatch(look: NavSeparationLook): Patch {
  return {
    background: look.background, backgroundColor: look.background, backgroundImage: '',
    border: '', borderTop: '', borderRight: '', borderLeft: '', outline: '', outlineOffset: '',
    borderWidth: '0px', borderStyle: 'solid', borderColor: look.line?.includes('--wb-text') ? 'var(--wb-text)' : 'var(--wb-border)',
    borderBottom: look.line ?? '',
  }
}

export function activeNavSeparationId(theme: ProjectTheme): string | null {
  const styles = theme.sectionComponents.find((item) => item.id === 'section-nav')?.styles
  if (!styles) return null
  const fill = styles.backgroundColor ?? styles.background
  return NAV_SEPARATION_LOOKS.find((look) => look.background === fill && (look.line ?? undefined) === (styles.borderBottom || undefined))?.id ?? null
}

export function applyNavSeparationToTheme(theme: ProjectTheme, look: NavSeparationLook): ProjectTheme {
  const patch = navSeparationPatch(look)
  return {
    ...theme,
    presetId: 'custom',
    sectionComponents: theme.sectionComponents.map((item) => (
      item.id === 'section-nav' ? { ...item, styles: mergeStylePatch(item.styles, patch) } : item
    )),
  }
}
